import { Component, Inject, OnInit } from '@angular/core';
import { RelayNotificationsService } from '../../../services/relay-notifications.service';
import { RelayNotificationPreferencesMasterObject } from '../../../models/relay-notifications-models';
import _, { result } from 'underscore';
import { IdNamePair } from '../../../models/id-name-pair';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';
import { ModalStates } from '../../../models/modal';
import { AclService } from '../../../services/acl.service';
import { RouteParamsService } from '../../../services/route-params.service';
import { UserManagementService } from '../../../services/user-management.service';
import { InstanceTypes } from '../../../models/instance-types';
import { Observable, of } from 'rxjs';
import { ClientFeatureWhitelistService } from '../../../services/client-feature-whitelist.service';
import { mergeMap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';

@Component({
  selector: 'lib-notification-preferences',
  templateUrl: './notification-preferences.component.html',
  styleUrls: ['./notification-preferences.component.css'],
  standalone: false
})
export class NotificationPreferencesComponent implements OnInit {
  public notificationPreferences: RelayNotificationPreferencesMasterObject;
  public displayModel: Category[] = [];
  public requiredNotifications: IdNamePair[] = [];
  public selectedNotification: number | null = null;
  public isClientAdmin = false;
  public isEmployeeUser = false;
  public hasPhoneNumber = false;
  public enableTestNotifications = false;
  public clientCode: string;
  private canViewAddress = false;
  private canViewDD = false;
  private canViewTimeOff = false;
  constructor(
    private service: RelayNotificationsService,
    private authService: AuthService,
    private modalService: ModalService,
    private aclService: AclService,
    private routeParamsService: RouteParamsService,
    private userService: UserManagementService,
    private whitelistService: ClientFeatureWhitelistService,
    private store: Store,
    @Inject('env') private env: any
  ) {
    this.save = _.debounce(this.save, 300);
  }

  ngOnInit() {
    this.isEmployeeUser = this.env.instanceType === InstanceTypes.EmployeePortal;
    this.enableTestNotifications = this.env.enableTestNotifications;
    this.userService.userHasPhoneNumber(this.authService.getCurrentUserId()).subscribe(result => {
      this.hasPhoneNumber = result.Result;
    });
    this.routeParamsService.getParam('client_code').subscribe(code => {
      this.clientCode = code;
      this.aclService.CurrentUserHasMinimumRole('Client Portal Admin', code).subscribe(result => {
        this.isClientAdmin = result;
        if (!this.isClientAdmin) {
          //check for the new role
          this.aclService.CurrentUserIsInRole('Notifications', code).subscribe(result => {
            this.isClientAdmin = result;
          });
        }
      });
      this.whitelistService
        .checkWhitelistForFeature(this.clientCode, 'addressChange')
        .pipe(
          mergeMap(whitelistResult => {
            if (whitelistResult && !this.isEmployeeUser) {
              return this.aclService.CurrentUserAccessCheck(
                'Client Portal Admin',
                'addressChange',
                this.clientCode
              );
            }
            return of(whitelistResult);
          })
        )
        .subscribe(result => (this.canViewAddress = result));
      this.whitelistService
        .checkWhitelistForFeature(this.clientCode, 'directDepositChange')
        .pipe(
          mergeMap(whitelistResult => {
            if (whitelistResult && !this.isEmployeeUser) {
              return this.aclService.CurrentUserAccessCheck(
                'Client Portal Admin',
                'directDepositChange',
                this.clientCode
              );
            }
            return of(whitelistResult);
          })
        )
        .subscribe(result => (this.canViewDD = result));
      this.store
        .selectOnce(ClientSelectionState.canEnableTimeOff)
        .pipe(
          mergeMap(result => {
            if (result && !this.isEmployeeUser) {
              return this.aclService.CurrentUserAccessCheck(
                'Client Portal Admin',
                'timeOff',
                this.clientCode
              );
            }
            return of(result);
          })
        )
        .subscribe(result => (this.canViewTimeOff = result));
    });
    this.service.getPreferences().subscribe(result => {
      this.notificationPreferences = result;
      this.notificationPreferences.Notifications.forEach(cat => {
        const category: Category = {
          name: cat.Name,
          groups: []
        };
        cat.Preferences.forEach(pref => {
          if (!category.groups.some(x => x.id === pref.NotificationGroupId)) {
            category.groups.push({
              id: pref.NotificationGroupId,
              name: pref.DisplayName,
              allowSms: pref.AllowSms,
              sendEmail: pref.SendEmail,
              sendSms: pref.SendSms,
              notificationName: pref.NotificationName,
              groupName: pref.NotificationGroupName
            });
          }
        });
        this.displayModel.push(category);
      });
    });
    this.service.getRequiredNotificationMetadata().subscribe(result => {
      this.requiredNotifications = result;
    });
  }

  canDisplayRow(group: Group) {
    if (group.groupName == 'directDepositChange') {
      return this.canViewDD;
    }
    if (group.groupName.includes('addressChange')) {
      return this.canViewAddress;
    }
    if (group.groupName.includes('clientTimeOff') || group.groupName.includes('employeeTimeOff')) {
      return this.canViewTimeOff;
    }
    return true;
  }
  canDisplayCategory(catName: string) {
    if (catName == 'Payroll') {
      return this.isClientAdmin;
    }
    if (catName == 'Request Management') {
      return this.canViewDD || this.canViewAddress || this.canViewTimeOff;
    }
    return true;
  }
  save() {
    this.service.savePreferences(this.notificationPreferences).subscribe(() => {});
  }
  securityChanged(value) {
    this.notificationPreferences.SmsForSecurity = value;
    this.save();
  }
  emailChanged(pref: Group, value) {
    pref.sendEmail = value;
    const category = this.notificationPreferences.Notifications.find(x =>
      x.Preferences.some(y => y.NotificationGroupId === pref.id)
    );

    category.Preferences.forEach(preference => {
      if (preference.NotificationGroupId === pref.id) {
        preference.SendEmail = value;
      }
    });

    this.save();
  }

  smsChanged(pref: Group, value) {
    pref.sendSms = value;
    const category = this.notificationPreferences.Notifications.find(x =>
      x.Preferences.some(y => y.NotificationGroupId === pref.id)
    );

    category.Preferences.forEach(preference => {
      if (preference.NotificationGroupId === pref.id) {
        preference.SendSms = value;
      }
    });
    this.save();
  }

  sendTest() {
    if (this.selectedNotification == null || !this.enableTestNotifications) {
      return;
    }
    let notificationName: string;
    if (this.requiredNotifications.some(y => y.Id == this.selectedNotification)) {
      for (let required of this.requiredNotifications) {
        if (required.Id == this.selectedNotification) {
          notificationName = required.Name;
          break;
        }
      }
    } else {
      notificationName = this.notificationPreferences.Notifications.find(x =>
        x.Preferences.some(y => y.NotificationId == this.selectedNotification)
      ).Preferences.find(y => y.NotificationId == this.selectedNotification).NotificationName;
    }
    if (notificationName == null || notificationName == '') {
      this.modalService.showAlertModal('Error finding test notification data', ModalStates.Error);
    }
    const email = this.authService.getCurrentEmail();
    this.service
      .sendTestNotification(this.selectedNotification, notificationName, email)
      .subscribe(() => {
        this.modalService.showAlertModal('Test Sent', ModalStates.Success);
      });
  }
}

interface Category {
  name: string;
  groups: Group[];
}
interface Group {
  id: number;
  name: string;
  allowSms: boolean;
  sendSms: boolean;
  sendEmail: boolean;
  notificationName: string;
  groupName: string;
}
